import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  limit as limitFn,
  orderBy,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';

import type { QAHistory } from '@goenhub/shared';

import { db } from '@/lib/firebase';
import { safeUpdateDoc } from '@/lib/safeWrite';

export interface QaHistoryRecord extends Omit<QAHistory, 'embedding'> {
  id: string;
  embedding?: number[];
}

export function useQaHistory(options: {
  adminReviewed?: boolean;
  lowRating?: boolean;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['qa_history', options],
    queryFn: async (): Promise<QaHistoryRecord[]> => {
      const conditions = [];
      if (options.adminReviewed !== undefined) {
        conditions.push(where('adminReviewed', '==', options.adminReviewed));
      }
      if (options.lowRating) {
        conditions.push(where('rating', '<=', 2));
      }
      const q = conditions.length > 0
        ? query(
            collection(db, 'qa_history'),
            ...conditions,
            orderBy(options.lowRating ? 'rating' : 'createdAt', options.lowRating ? 'asc' : 'desc'),
            limitFn(options.limit ?? 100),
          )
        : query(
            collection(db, 'qa_history'),
            orderBy('createdAt', 'desc'),
            limitFn(options.limit ?? 100),
          );
      const snap = await getDocs(q);
      return snap.docs.map((d) => {
        const data = d.data();
        // embedding は重いので UI では省く
        const { embedding: _embedding, ...rest } = data;
        void _embedding;
        return { id: d.id, ...(rest as Omit<QAHistory, 'embedding'>) };
      });
    },
  });
}

export function useReviewQa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      adminApproved: boolean;
      adminCorrectedAnswer: string | null;
    }) => {
      await safeUpdateDoc(doc(db, 'qa_history', args.id), {
        adminReviewed: true,
        adminApproved: args.adminApproved,
        adminCorrectedAnswer: args.adminCorrectedAnswer,
        reviewedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['qa_history'] }),
  });
}
